import { useContext } from 'react'

import { OptimizelyContext } from '@optimizely/react-sdk'
import type { ActionEvents } from '@space-core/datahub'
import { Entities, Actions } from '@space-core/datahub'
import { loginEventDispatch } from '@space-events/login'
import { useLogout } from '@space-shell/common/components/baseLayout/hooks/useLogout'
import type { ApplicationLanguageType } from '@space-shell/common/components/customLanguage/constants'
import { AVAILABLE_LANGUAGES } from '@space-shell/common/components/customLanguage/constants'
import { useDataHubEvents } from '@space-shell/common/hooks/useDataHubEvents'
import { useGoogleAnalytics } from '@space-shell/common/hooks/useGoogleAnalytics'
import { CookieUtils } from '@space-shell/common/utils/cookie'
import { CookieKeys } from '@space-shell/common/utils/cookie/constants'
import { getHelpMenuLink, PURCHASES_MENU } from '@space-shell/constant/baseLayout'
import { useNavigate, useLocation, useParams } from 'react-router-dom'

export function useHeaderEventHandlers() {
  const { sendDataHubEvent } = useDataHubEvents()
  const { sendGoogleAnalyticsEvent } = useGoogleAnalytics()
  const optimizely = useContext(OptimizelyContext).optimizely
  const navigate = useNavigate()
  const location = useLocation()
  const { lang } = useParams()
  const { logout } = useLogout()

  function handleHelpClick() {
    sendDataHubEvent({ group: Entities.HEADER_USER_MENU, name: Actions.CLICK_MENU_HELP })
    window.open(getHelpMenuLink(lang as string), '_blank')
  }

  function handleMyPurchases() {
    sendDataHubEvent({ group: Entities.HEADER_USER_MENU, name: Actions.CLICK_MENU_MY_PURCHASES })
    window.open(PURCHASES_MENU, '_blank')
  }

  function handleSignUp(eventOption?: string) {
    sendDataHubEvent({ group: Entities.HEADER_USER_MENU, name: Actions.CLICK_SIGNUP })
    sendGoogleAnalyticsEvent({ group: Entities.HEADER_USER_MENU, name: Actions.CLICK_SIGNUP })

    if (eventOption) {
      optimizely?.track(eventOption)
    }

    window.open(`${process.env.SIGNUP_URL}${window.location.href}&systemOrigin=app-space`, '_self')
  }

  function handleSignIn(eventOption?: string) {
    sendDataHubEvent({ group: Entities.HEADER_USER_MENU, name: Actions.CLICK_SIGNIN })
    sendGoogleAnalyticsEvent({ group: Entities.HEADER_USER_MENU, name: Actions.CLICK_SIGNIN })

    if (eventOption) {
      optimizely?.track(eventOption)
      optimizely?.track('click_login_primary')
    }

    loginEventDispatch({ signInRedirect: true })
  }

  function handleMyAccount() {
    sendDataHubEvent({ group: Entities.HEADER_USER_MENU, name: Actions.CLICK_MY_ACCOUNT })
    window.open(`${process.env.MY_ACCOUNT_URL}?systemOrigin=club-new`, '_blank')
  }

  function handleLanguageChange(languageValue: ApplicationLanguageType) {
    if (AVAILABLE_LANGUAGES[languageValue]?.eventName) {
      sendDataHubEvent({
        group: Entities.HEADER_LANGUAGE_OPTIONS,
        name: AVAILABLE_LANGUAGES[languageValue]?.eventName as ActionEvents
      })
    }

    CookieUtils.setCookie(CookieKeys.COOKIE_LANGUAGE, languageValue)

    const newPath = location.pathname.replace(`/${lang}`, `/${languageValue}`)
    navigate(newPath + location.search)
    window.location.reload()
  }

  return {
    handleHelpClick,
    handleMyPurchases,
    handleSignUp,
    handleSignIn,
    handleMyAccount,
    handleLanguageChange,
    handleLogout: logout
  } as const
}
